import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useBlockEditFormStore } from '@ContractBuilder/modules/block-edit';
import { useEntityStore } from '@ContractBuilder/store';
import { useDatapointsStore } from '@ContractBuilder/store/datapoints.store';
import { canChange } from '@helpers/canChange';
import { useQuery, useQueryClient } from '@tanstack/react-query';

export const REFRESH_INTERVAL_MS = 20 * 60 * 1000;

const autoRefreshKey = (id: string) => ['entity', id, 'auto-refresh'] as const;

interface UseContractAutoRefreshReturn {
  isRefreshing: boolean;
}

type UseContractAutoRefreshFn = (id: string) => UseContractAutoRefreshReturn;

export const useContractAutoRefresh: UseContractAutoRefreshFn = (id) => {
  const { pathname } = useLocation();
  const hasMountedRef = useRef(false);
  const triggerRefresh = useTriggerContractRefresh(id);

  const isDirty = useDatapointsStore((s) => s.isDirty);
  const reloadSubmission = useEntityStore((s) => s.reloadSubmission);
  const submissionStatus = useEntityStore((s) => s.submission?.status);
  const formValuesId = useBlockEditFormStore((s) => s.formValues?.id);
  const hasChanges = useBlockEditFormStore((s) => s.hasChanges);

  const isReadOnly = !canChange(submissionStatus);
  // isLoading intentionally excluded — triggerRefresh and queryFn both guard via getState().isLoading.
  // Subscribing to isLoading here causes ContractBuilderLayout (which wraps the entire editor)
  // to re-render on every operation, cascading through the whole tree.
  const shouldSkip = isReadOnly || isDirty || formValuesId !== undefined || hasChanges;

  const { isFetching } = useQuery({
    queryKey: autoRefreshKey(id),
    queryFn: async () => {
      if (useEntityStore.getState().isLoading) {
        return null;
      }

      const result = await reloadSubmission();
      return result ?? null;
    },
    // initialData seeds the cache so the query treats this as "has data" rather than
    // "needs initial fetch" — without it, refetchOnMount: false has no effect because
    // it only suppresses *re*fetches of existing data. With both in place, the first
    // network call only happens on focus/interval/route-change.
    initialData: null,
    meta: { silent: true },
    enabled: !shouldSkip,
    refetchOnWindowFocus: 'always',
    refetchInterval: REFRESH_INTERVAL_MS,
    refetchIntervalInBackground: false,
    refetchOnMount: false,
    retry: false,
    staleTime: REFRESH_INTERVAL_MS,
  });

  // Route change trigger — skip initial mount, fire on every subsequent pathname change.
  // id is intentionally omitted from deps: in-app, id always changes with pathname so
  // TanStack Query's shouldFetchOptionally handles entity-switch fetches via the query-key
  // change. Including id here would fire a redundant refetchQueries on top of that.
  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }

    triggerRefresh();
  }, [pathname, triggerRefresh]);

  return { isRefreshing: isFetching };
};

export const useTriggerContractRefresh = (id: string) => {
  const queryClient = useQueryClient();

  return useCallback(() => {
    const isLoading = useEntityStore.getState().isLoading;
    const isDirty = useDatapointsStore.getState().isDirty;
    const { formValues, hasChanges } = useBlockEditFormStore.getState();

    if (isLoading || isDirty || formValues?.id !== undefined || hasChanges) return;

    queryClient.refetchQueries({ queryKey: autoRefreshKey(id), type: 'active' });
  }, [queryClient, id]);
};
